import { createRef, PropsWithChildren, useContext, useEffect } from "react";

import { css } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";

import { ScrollContext } from "./scroller";

type ScrollerItemProps = PropsWithChildren<{
    index: number;
}>;

export const ScrollerItem = ({ children, index }: ScrollerItemProps) => {
    const { ref: scrollRef, inView: sectionInView } = useInView({
        threshold: 0.25
    });
    const itemRef = createRef<HTMLDivElement>();
    const { setActiveIndex, graphicHeight, graphicTopBottom } =
        useContext(ScrollContext);

    useEffect(() => {
        if (!document) {
            return;
        }

        const checkElementPosition = () => {
            if (!itemRef.current || !graphicTopBottom || !sectionInView) {
                return;
            }

            const rect = itemRef.current.getBoundingClientRect();
            const top = rect.top;
            const bottom = rect.bottom;

            // check if this element overlaps with the top or bottom of the graphic
            if (
                (top >= graphicTopBottom.top &&
                    top <= graphicTopBottom.bottom) ||
                (bottom >= graphicTopBottom.top &&
                    bottom <= graphicTopBottom.bottom)
            ) {
                setActiveIndex(index);
            }
        };

        document.addEventListener("scroll", checkElementPosition);

        return () => {
            document.removeEventListener("scroll", checkElementPosition);
        };
    }, [setActiveIndex, index, itemRef, graphicTopBottom, sectionInView]);

    return (
        <div
            css={itemWrapperStyle}
            ref={scrollRef}
            style={{
                minHeight: graphicHeight ? graphicHeight + "px" : "auto"
            }}
        >
            <div css={itemStyle} ref={itemRef}>
                {children}
            </div>
        </div>
    );
};

const mediaQuery =
    "@media (max-height: 49.99vw) and (max-width: 1271.99px), (max-height: 635.99px) and (min-width: 1272px), not all and (min-width: 48rem)" as const;

const itemWrapperStyle = css({
    position: "relative",
    height: "100%",
    maxHeight: "70rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",

    [":not(:last-of-type):not(:first-of-type)"]: {
        height: "100vh"
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    [mediaQuery]: {
        height: "auto !important",

        [":not(:last-of-type):not(:first-of-type)"]: {
            paddingBlock: 60
        }
    }
});

const itemStyle = css({
    display: "flex",
    opacity: 1,
    gap: 50,
    flexDirection: "column"
});
